import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface Notice {
  id: number;
  title: string;
  content: string;
  is_important: boolean;
  author_id: string;
  views: number;
  created_at: string;
  updated_at: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const ITEMS_PER_PAGE = 10;

export default function AdminNoticesPage() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editNotice, setEditNotice] = useState<Notice | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isImportant, setIsImportant] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const fetchNotices = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const from = (page - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const [{ data, error }, { count }] = await Promise.all([
        supabase
          .from("notices")
          .select("*")
          .order("created_at", { ascending: false })
          .range(from, to),
        supabase
          .from("notices")
          .select("*", { count: "exact", head: true }),
      ]);

      if (error) throw error;
      setNotices(data || []);
      if (count !== null) setTotalCount(count);
    } catch {
      setMessage({ type: "error", text: "공지사항 목록을 불러오지 못했습니다" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices(currentPage);
  }, [currentPage, fetchNotices]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const resetForm = useCallback(() => {
    setTitle("");
    setContent("");
    setIsImportant(false);
    setEditNotice(null);
    setShowForm(false);
  }, []);

  const openCreateForm = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const openEditForm = useCallback((notice: Notice) => {
    setEditNotice(notice);
    setTitle(notice.title);
    setContent(notice.content);
    setIsImportant(notice.is_important);
    setShowForm(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setMessage({ type: "error", text: "제목을 입력해주세요" });
      return;
    }
    if (!content.trim()) {
      setMessage({ type: "error", text: "내용을 입력해주세요" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (editNotice) {
        const { error } = await supabase
          .from("notices")
          .update({
            title: title.trim(),
            content: content.trim(),
            is_important: isImportant,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editNotice.id);
        if (error) throw error;
        setMessage({ type: "success", text: "공지사항이 수정되었습니다" });
      } else {
        const { data: newNotice, error } = await supabase
          .from("notices")
          .insert({
            title: title.trim(),
            content: content.trim(),
            is_important: isImportant,
            author_id: user?.id || null,
          })
          .select("id, title")
          .single();
        if (error) throw error;

        // 새 공지 등록 시 알림 발송
        if (newNotice) {
          await supabase.from("notifications").insert({
            type: "notice",
            title: "새 공지사항",
            message: title.trim(),
            related_id: newNotice.id,
          });
        }

        setMessage({ type: "success", text: "공지사항이 등록되었습니다" });
        setCurrentPage(1);
      }

      resetForm();
      fetchNotices(currentPage);
    } catch {
      setMessage({ type: "error", text: "저장에 실패했습니다" });
    } finally {
      setSaving(false);
    }
  }, [title, content, isImportant, editNotice, resetForm, fetchNotices, currentPage]);

  const handleToggleImportant = useCallback(
    async (notice: Notice) => {
      try {
        const { error } = await supabase
          .from("notices")
          .update({ is_important: !notice.is_important, updated_at: new Date().toISOString() })
          .eq("id", notice.id);
        if (error) throw error;

        setNotices((prev) =>
          prev.map((n) => (n.id === notice.id ? { ...n, is_important: !n.is_important } : n)),
        );
      } catch {
        setMessage({ type: "error", text: "상태 변경에 실패했습니다" });
      }
    },
    [],
  );

  const handleDelete = useCallback(async (notice: Notice) => {
    setDeletingId(notice.id);
    try {
      const { error } = await supabase.from("notices").delete().eq("id", notice.id);
      if (error) throw error;

      setMessage({ type: "success", text: "공지사항이 삭제되었습니다" });
      fetchNotices(currentPage);
    } catch {
      setMessage({ type: "error", text: "삭제에 실패했습니다" });
    } finally {
      setDeletingId(null);
    }
  }, [currentPage, fetchNotices]);

  const getPageNumbers = useCallback(() => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, currentPage]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">공지사항 관리</h2>
          <p className="text-xs text-foreground-600 mt-0.5">총 {totalCount}개</p>
        </div>

        <button
          onClick={showForm ? resetForm : openCreateForm}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
            showForm
              ? "bg-background-200 text-foreground-600 hover:bg-background-300"
              : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
          }`}
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={showForm ? "ri-close-line" : "ri-add-line"} />
          </span>
          {showForm ? "취소" : "공지사항 작성"}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-4">
            {editNotice ? "공지사항 수정" : "새 공지사항 작성"}
          </h3>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">제목</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="공지사항 제목을 입력하세요"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div className="pt-5">
                <button
                  onClick={() => setIsImportant(!isImportant)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isImportant
                      ? "bg-red-500/15 text-red-400 border border-red-500/30"
                      : "bg-background-200 text-foreground-600 border border-background-300 hover:bg-background-300"
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={isImportant ? "ri-alert-fill" : "ri-alert-line"} />
                  </span>
                  {isImportant ? "중요" : "일반"}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="공지사항 내용을 입력하세요"
                rows={8}
                className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors resize-vertical"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-check-line" />
                </span>
                {saving ? "저장 중..." : editNotice ? "수정하기" : "등록하기"}
              </button>
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-background-200 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notice List */}
      <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notices.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-megaphone-line text-xl text-foreground-600" />
            </div>
            <p className="text-sm text-foreground-600">등록된 공지사항이 없습니다</p>
            <p className="text-xs text-foreground-600 mt-1">상단의 버튼을 눌러 공지사항을 작성해보세요</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-300 bg-background-200/50">
                    <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-16">번호</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">제목</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-20">중요</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-20">조회수</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-28 hidden md:table-cell">작성일</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-24">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {notices.map((notice) => (
                    <tr
                      key={notice.id}
                      className="border-b border-background-300 last:border-0 hover:bg-background-200/40 transition-colors"
                    >
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-foreground-600">{notice.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[300px] lg:max-w-[400px]">
                          <span className="text-xs font-medium text-foreground-700 line-clamp-1">{notice.title}</span>
                          {notice.is_important && (
                            <span className="inline-block ml-2 px-1.5 py-0.5 text-[9px] font-bold bg-red-500/15 text-red-400 rounded">
                              중요
                            </span>
                          )}
                          <p className="text-[10px] text-foreground-600 mt-0.5 line-clamp-1">{notice.content}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleImportant(notice)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            notice.is_important
                              ? "bg-red-500/15 text-red-400"
                              : "bg-background-200 text-foreground-600 hover:bg-background-300"
                          }`}
                        >
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className={notice.is_important ? "ri-alert-fill" : "ri-information-line"} />
                          </span>
                          {notice.is_important ? "중요" : "일반"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-foreground-600">{notice.views}</span>
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        <span className="text-[10px] text-foreground-600">{formatDate(notice.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openEditForm(notice)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-background-200 hover:bg-background-300 text-foreground-600 transition-colors cursor-pointer"
                          >
                            <span className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-edit-line" />
                            </span>
                          </button>
                          <button
                            onClick={() => handleDelete(notice)}
                            disabled={deletingId === notice.id}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            <span className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className={deletingId === notice.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} />
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 py-4 border-t border-background-300">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-700 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-s-line" />
                  </span>
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs text-foreground-600">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page as number)}
                      className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                        currentPage === page
                          ? "bg-primary-500 text-foreground-50"
                          : "text-foreground-600 hover:text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-700 hover:bg-background-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}